import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { HealthBar } from '../../HealthBar.js';
import { PALETTE, matte, colored } from '../../Style.js';

const IRON = PALETTE.iron;
const ROPE = 0xc9b48a;
const SKIN = 0xd9a77e;
const GOLD = 0xd6a640;

// Everything stays inside the server's 1.5 collision radius so nothing pokes into walls
// The pivot sits forward of centre so the long arm stays inside the radius as it sweeps
const PIVOT_Y = 1.9;
const PIVOT_Z = 0.3;
const LONG_ARM = 1.5;
const SHORT_ARM = 0.5;
const WHEEL_RADIUS = 0.38;
// The whole engine is built at this size; it fills the server's 2.0 collision radius
const SCALE = 1.35;

// Arm angles: positive raises the long (sling) end
const ARM_COCKED = -0.5;
const ARM_RELEASED = 1.85;

// Throw cycle timings in seconds; the whole cycle fits inside the server's 1 shot per 3.3s
const THROW_TIME = 0.35;
const HOLD_TIME = 0.25;
const REWIND_TIME = 1.8;

// Widen the bottom of a geometry so panels taper towards the top
function flare(geometry, amount) {
  const pos = geometry.attributes.position;
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox;
  for (let i = 0; i < pos.count; i++) {
    const t = (max.y - pos.getY(i)) / (max.y - min.y);
    pos.setX(i, pos.getX(i) * (1 + amount * t));
  }
  return geometry;
}

// A straight timber running between two points in the y/z plane
function beam(width, from, to, color, x = 0) {
  const dy = to[0] - from[0];
  const dz = to[1] - from[1];
  return colored(new THREE.BoxGeometry(width, Math.hypot(dy, dz), width), color, {
    x, y: (from[0] + to[0]) / 2, z: (from[1] + to[1]) / 2, rx: Math.atan2(dz, dy)
  });
}

// Wheeled trebuchet: timber A-frame, counterweighted throwing arm and sling,
// team cloth on the frame and a pennant, pushed along by a small crewman
export class RocketLauncher {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 20; // RocketLauncherHealth from server
    this.geometries = [];
    this.wheels = [];
    this.walk = 0;
    this.walkPhase = 0;
    this.lastAnimTime = null;
    this.lastMeshPosition = new THREE.Vector3();
    this.timeOffset = Math.random() * 10;
    this.throwStart = null;
    this.create();
  }

  // Merge coloured parts into a single mesh sharing the vertex-coloured material
  part(parts) {
    const geometry = mergeGeometries(parts);
    parts.forEach(p => p.dispose());
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  // A pivoting group holding one merged part
  pivot(parent, parts, x, y, z) {
    const group = new THREE.Group();
    group.position.set(x, y, z);
    group.add(this.part(parts));
    parent.add(group);
    return group;
  }

  create() {
    const bodyGroup = new THREE.Group();

    // Nothing here changes per instance, so the cached material is shared
    this.material = matte(0xffffff, { vertexColors: true });

    const team = new THREE.Color(this.color);
    const teamDark = team.clone().multiplyScalar(0.65);

    // The carriage rocks gently as it rolls
    this.body = new THREE.Group();
    this.body.scale.setScalar(SCALE);
    bodyGroup.add(this.body);

    const frame = [
      // Wheeled base: side rails, cross beams, plank deck and axles
      colored(new THREE.BoxGeometry(0.16, 0.18, 2.2), PALETTE.woodDark, { x: -0.55, y: 0.5 }),
      colored(new THREE.BoxGeometry(0.16, 0.18, 2.2), PALETTE.woodDark, { x: 0.55, y: 0.5 }),
      colored(new THREE.BoxGeometry(1.26, 0.14, 0.16), PALETTE.woodDark, { y: 0.5, z: -1.0 }),
      colored(new THREE.BoxGeometry(1.26, 0.14, 0.16), PALETTE.woodDark, { y: 0.5, z: 1.0 }),
      colored(new THREE.BoxGeometry(0.94, 0.06, 1.84), PALETTE.woodLight, { y: 0.52 }),
      colored(new THREE.BoxGeometry(1.48, 0.1, 0.1), IRON, { y: WHEEL_RADIUS, z: -0.75 }),
      colored(new THREE.BoxGeometry(1.48, 0.1, 0.1), IRON, { y: WHEEL_RADIUS, z: 0.75 }),
      // Iron axle the arm swings on
      colored(new THREE.CylinderGeometry(0.07, 0.07, 1.2, 6), IRON, { y: PIVOT_Y, z: PIVOT_Z, rz: Math.PI / 2 })
    ];

    // Two A-frame uprights with a cross brace and a team cloth panel on each side
    for (const side of [-1, 1]) {
      const x = side * 0.5;
      frame.push(beam(0.14, [0.55, PIVOT_Z - 0.72], [PIVOT_Y + 0.05, PIVOT_Z], PALETTE.wood, x));
      frame.push(beam(0.14, [0.55, PIVOT_Z + 0.72], [PIVOT_Y + 0.05, PIVOT_Z], PALETTE.wood, x));
      frame.push(colored(new THREE.BoxGeometry(0.1, 0.1, 0.9), PALETTE.woodDark, { x, y: 1.1, z: PIVOT_Z }));
      frame.push(colored(new THREE.BoxGeometry(0.2, 0.16, 0.24), PALETTE.woodDark, { x, y: PIVOT_Y + 0.08, z: PIVOT_Z }));
      frame.push(colored(flare(new THREE.BoxGeometry(0.42, 0.62, 0.03), 1.6), team, {
        x: x + side * 0.08, y: 0.95, z: PIVOT_Z, ry: Math.PI / 2
      }));
      frame.push(colored(new THREE.BoxGeometry(0.03, 0.18, 0.18), GOLD, {
        x: x + side * 0.1, y: 0.92, z: PIVOT_Z, rx: Math.PI / 4
      }));
    }

    // Pennant pole at the front corner
    frame.push(colored(new THREE.CylinderGeometry(0.035, 0.045, 2.75, 5), PALETTE.woodDark, { x: -0.62, y: 1.9, z: 0.95 }));
    frame.push(colored(new THREE.SphereGeometry(0.07, 5, 3), GOLD, { x: -0.62, y: 3.3, z: 0.95 }));
    this.body.add(this.part(frame));

    // Team pennant trailing back from the pole, fluttering separately
    this.pennant = this.pivot(this.body, [
      colored(new THREE.ConeGeometry(0.22, 0.8, 4), team, { y: 0, z: -0.4, rx: -Math.PI / 2, scale: [0.12, 1, 1] })
    ], -0.62, 3.1, 0.95);

    // Spoked wheels, spun as the carriage moves
    for (const sx of [-0.7, 0.7]) {
      for (const sz of [-0.75, 0.75]) {
        const wheel = this.pivot(this.body, [
          colored(new THREE.CylinderGeometry(WHEEL_RADIUS, WHEEL_RADIUS, 0.12, 8), PALETTE.wood, { rz: Math.PI / 2 }),
          colored(new THREE.CylinderGeometry(WHEEL_RADIUS + 0.02, WHEEL_RADIUS + 0.02, 0.08, 8), IRON, { rz: Math.PI / 2 }),
          colored(new THREE.BoxGeometry(0.14, WHEEL_RADIUS * 1.7, 0.06), PALETTE.woodDark, { x: Math.sign(sx) * 0.02 }),
          colored(new THREE.BoxGeometry(0.14, 0.06, WHEEL_RADIUS * 1.7), PALETTE.woodDark, { x: Math.sign(sx) * 0.02 }),
          colored(new THREE.CylinderGeometry(0.08, 0.08, 0.18, 6), IRON, { rz: Math.PI / 2 })
        ], sx, WHEEL_RADIUS, sz);
        this.wheels.push(wheel);
      }
    }

    // Throwing arm: long end towards the back (-z), short counterweight end to the front
    this.arm = this.pivot(this.body, [
      colored(new THREE.BoxGeometry(0.18, 0.18, LONG_ARM + SHORT_ARM + 0.1), PALETTE.wood, { z: (SHORT_ARM - LONG_ARM) / 2 }),
      colored(new THREE.BoxGeometry(0.18, 0.18, 0.12), IRON, { z: -LONG_ARM + 0.1 }),
      colored(new THREE.BoxGeometry(0.18, 0.18, 0.12), IRON, {})
    ], 0, PIVOT_Y, PIVOT_Z);

    // Counterweight box hangs from the short end, draped in team colour
    this.counterweight = this.pivot(this.arm, [
      colored(new THREE.BoxGeometry(0.06, 0.3, 0.06), IRON, { y: -0.15 }),
      colored(new THREE.BoxGeometry(0.5, 0.48, 0.5), PALETTE.woodDark, { y: -0.52 }),
      colored(new THREE.BoxGeometry(0.54, 0.08, 0.54), IRON, { y: -0.4 }),
      colored(new THREE.BoxGeometry(0.54, 0.08, 0.54), IRON, { y: -0.66 }),
      colored(new THREE.BoxGeometry(0.56, 0.06, 0.56), team, { y: -0.26 }),
      colored(new THREE.BoxGeometry(0.56, 0.3, 0.04), teamDark, { y: -0.4, z: 0.27 }),
      colored(new THREE.BoxGeometry(0.56, 0.3, 0.04), teamDark, { y: -0.4, z: -0.27 })
    ], 0, 0, SHORT_ARM);

    // Rope sling with a stone in its pouch, hanging from the long end
    this.sling = this.pivot(this.arm, [
      colored(new THREE.BoxGeometry(0.03, 0.5, 0.03), ROPE, { y: -0.25 }),
      colored(new THREE.BoxGeometry(0.16, 0.08, 0.2), PALETTE.woodDark, { y: -0.52 }),
      colored(new THREE.DodecahedronGeometry(0.1, 0), PALETTE.stone, { y: -0.5 })
    ], 0, 0, -LONG_ARM);

    // A small crewman leaning into the back of the carriage
    this.crew = new THREE.Group();
    this.crew.position.set(0.38, 0.045, -1.08); // Lifted so the leaning crewman's toes touch the ground
    this.crew.rotation.x = 0.35;
    this.body.add(this.crew);
    this.crewBody = this.pivot(this.crew, [
      colored(new THREE.BoxGeometry(0.32, 0.4, 0.22), team, { y: 0.62 }),
      colored(new THREE.BoxGeometry(0.34, 0.06, 0.24), PALETTE.woodDark, { y: 0.45 }),
      colored(new THREE.BoxGeometry(0.22, 0.22, 0.22), SKIN, { y: 0.95 }),
      colored(new THREE.ConeGeometry(0.16, 0.16, 6), teamDark, { y: 1.12 }),
      // Arms stretched forward to the cross beam
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.36), team, { x: -0.18, y: 0.72, z: 0.18 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.36), team, { x: 0.18, y: 0.72, z: 0.18 })
    ], 0, 0, 0);
    const crewLeg = () => [
      colored(new THREE.BoxGeometry(0.12, 0.38, 0.14), PALETTE.woodDark, { y: -0.2 }),
      colored(new THREE.BoxGeometry(0.13, 0.08, 0.2), IRON, { y: -0.4, z: 0.03 })
    ];
    this.crewLeftLeg = this.pivot(this.crew, crewLeg(), -0.08, 0.44, 0);
    this.crewRightLeg = this.pivot(this.crew, crewLeg(), 0.08, 0.44, 0);

    this.setArm(ARM_COCKED, 0);

    // Create health bar
    this.healthBar = new HealthBar(this.maxHealth, 2.6, 0.28);
    this.healthBar.getGroup().position.y = 5.1;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition.copy(this.mesh.position);

    this.scene.add(this.mesh);
  }

  // Pose the arm; the counterweight and sling hang from it, the sling whipping by `whip`
  setArm(angle, whip) {
    this.arm.rotation.x = angle;
    this.counterweight.rotation.x = -angle;
    this.sling.rotation.x = -angle + whip;
  }

  // Trigger a throw (safe to call every time a shot is fired)
  fire() {
    if (this.throwStart === null) {
      this.throwStart = performance.now() / 1000;
    }
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Wheels roll with distance moved, the crewman walks, the pennant flutters,
  // and the arm runs through its throw cycle
  animate() {
    const now = performance.now() / 1000;
    const dt = this.lastAnimTime === null ? 0 : Math.min(now - this.lastAnimTime, 0.1);
    this.lastAnimTime = now;
    if (dt <= 0) return;

    const pos = this.mesh.position;
    const moved = Math.min(Math.hypot(pos.x - this.lastMeshPosition.x, pos.z - this.lastMeshPosition.z), 1);
    this.lastMeshPosition.copy(pos);

    const target = Math.min(1, moved / dt / 2);
    this.walk += (target - this.walk) * Math.min(1, dt * 8);
    this.walkPhase += moved * 4.5;

    for (const wheel of this.wheels) {
      wheel.rotation.x += moved / (WHEEL_RADIUS * SCALE);
    }
    this.body.rotation.z = Math.sin(this.walkPhase * 0.5) * 0.015 * this.walk;

    const swing = Math.sin(this.walkPhase) * 0.4 * this.walk;
    this.crewLeftLeg.rotation.x = swing;
    this.crewRightLeg.rotation.x = -swing;
    this.crewBody.position.y = Math.abs(Math.sin(this.walkPhase)) * 0.03 * this.walk;

    const t = now + this.timeOffset;
    this.pennant.rotation.y = Math.sin(t * 3.1) * 0.25 + Math.sin(t * 7.3) * 0.08;

    if (this.throwStart === null) {
      // Cocked and waiting, the sling settling with the carriage's motion
      this.setArm(ARM_COCKED, Math.sin(this.walkPhase) * 0.05 * this.walk);
      return;
    }

    const elapsed = now - this.throwStart;
    if (elapsed < THROW_TIME) {
      // Counterweight drops, the arm whips up and over, the sling trailing then flinging
      const p = elapsed / THROW_TIME;
      const eased = p * p;
      this.setArm(ARM_COCKED + (ARM_RELEASED - ARM_COCKED) * eased, 0.2 * Math.sin(p * Math.PI) - 2.0 * eased);
    } else if (elapsed < THROW_TIME + HOLD_TIME) {
      // Arm rocks against its stop
      const p = (elapsed - THROW_TIME) / HOLD_TIME;
      this.setArm(ARM_RELEASED - Math.sin(p * Math.PI) * 0.12, -2.0 * (1 - p));
    } else if (elapsed < THROW_TIME + HOLD_TIME + REWIND_TIME) {
      // Winched back down to the cocked position
      const p = (elapsed - THROW_TIME - HOLD_TIME) / REWIND_TIME;
      const eased = p * p * (3 - 2 * p);
      this.setArm(ARM_RELEASED + (ARM_COCKED - ARM_RELEASED) * eased, 0);
    } else {
      this.throwStart = null;
      this.setArm(ARM_COCKED, 0);
    }
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    // The shared material is cached by Style.js and stays alive
    this.geometries.forEach(g => g.dispose());
    this.geometries = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
